// The Y.Doc binding shared by the server and the screen: both use
// `@tiptap/y-tiptap` (the binding of TipTap's Collaboration extension), so the
// two sides read and write the Y.XmlFragment the same way.

import type { Node as PMNode } from "@tiptap/pm/model";
import {
  prosemirrorToYXmlFragment,
  yXmlFragmentToProseMirrorRootNode,
} from "@tiptap/y-tiptap";
import * as Y from "yjs";
import { BLOCK_ID_ATTR, isSeedableBlockNode, seedBlockIds } from "./block-id";
import { extractBlocks, HASH_SEP, reassignIds } from "./blocks";
import { type EditorProfile, parseMarkdown } from "./markdown";
import { getEditorSchema } from "./schema";
import { sha1Hex } from "./sha1";

/** The name of the Y.XmlFragment holding the body (the same on the server and the screen). */
export const YDOC_FRAGMENT = "default";

/**
 * The document in a Y.Doc, in the profile's schema.
 *
 * Index guarantee: an element the schema cannot build (a node type it does not
 * know, from another editor generation) is deleted from the Y fragment during
 * the conversion (the binding's own behaviour). So after this call the
 * fragment's top-level elements and the returned doc's children correspond one
 * to one, by index, and a caller may address `fragment.get(i)` for child `i`.
 * The deletion is a Y change: callers keep a live copy only from the same
 * editor generation (a different generation is rebuilt from the text).
 */
export function pmDocFromYDoc(
  ydoc: Y.Doc,
  profile: EditorProfile = "document",
  fragmentName: string = YDOC_FRAGMENT,
): PMNode {
  return yXmlFragmentToProseMirrorRootNode(
    ydoc.getXmlFragment(fragmentName),
    getEditorSchema(profile),
  );
}

/** One empty paragraph: the smallest valid document (`block+`). */
export function emptyDoc(profile: EditorProfile = "document"): PMNode {
  const schema = getEditorSchema(profile);
  return schema.node("doc", null, [schema.node("paragraph")]);
}

/** Markdown as a document; empty markdown is {@link emptyDoc}. */
export function docFromMarkdown(
  markdown: string,
  profile: EditorProfile = "document",
): PMNode {
  return markdown === "" ? emptyDoc(profile) : parseMarkdown(markdown, profile);
}

/** The SHA-1 (hex) of `kind + "\0" + text`: the catalog's content hash. */
export function blockContentHash(kind: string, text: string): string {
  return sha1Hex(`${kind}${HASH_SEP}${text}`);
}

/**
 * Parses markdown and gives the top-level blocks ids ("document" profile;
 * "note" gets none).
 *
 * - `blockIds` (the catalog's ids in top-level order): when their count equals
 *   the number of blocks that take an id, they are given in order.
 * - Otherwise, with `hashes` (the catalog's content hashes, aligned with
 *   `blockIds`), an id goes only to a block with exactly the same content, in
 *   order with the smallest displacement (a block that changed gets a new id -
 *   never an id of another block).
 * - Every other block gets a new id.
 */
export function pmDocFromMarkdown(
  markdown: string,
  profile: EditorProfile = "document",
  blockIds?: readonly string[] | null,
  hashes?: readonly string[] | null,
): PMNode {
  const doc = docFromMarkdown(markdown, profile);
  if (profile !== "document") return doc;
  if (!blockIds || blockIds.length === 0) return seedBlockIds(doc);

  const seedable: number[] = [];
  doc.forEach((child, _offset, index) => {
    if (isSeedableBlockNode(child)) seedable.push(index);
  });
  const ids = new Map<number, string>();
  if (seedable.length === blockIds.length) {
    seedable.forEach((index, i) => {
      ids.set(index, blockIds[i]);
    });
  } else if (hashes && hashes.length === blockIds.length) {
    // Old blocks are known only by id and content hash: with no text, passes 1
    // and 3 of `reassignIds` never match, so only exact content (pass 2) carries.
    const oldBlocks = blockIds.map((id, i) => ({
      idx: i,
      id,
      kind: "",
      text: "",
      hash: hashes[i],
    }));
    const newBlocks = extractBlocks(doc)
      .filter((b) => seedable.includes(b.idx))
      .map((b) => ({ ...b, hash: blockContentHash(b.kind, b.text) }));
    const { assignments } = reassignIds(oldBlocks, newBlocks);
    for (const a of assignments) if (a.inheritedFrom) ids.set(a.idx, a.id);
  }
  if (ids.size === 0) return seedBlockIds(doc);
  const children: PMNode[] = [];
  doc.forEach((child, _offset, index) => {
    const id = ids.get(index);
    children.push(
      id === undefined
        ? child
        : child.type.create(
            { ...child.attrs, [BLOCK_ID_ATTR]: id },
            child.content,
            child.marks,
          ),
    );
  });
  return seedBlockIds(doc.type.create(doc.attrs, children, doc.marks));
}

/**
 * A fresh live copy (Y.Doc) from markdown and the catalog's block ids (see
 * {@link pmDocFromMarkdown} for how ids are given).
 */
export function ydocFromMarkdown(
  markdown: string,
  profile: EditorProfile = "document",
  blockIds?: readonly string[] | null,
  hashes?: readonly string[] | null,
): Y.Doc {
  const ydoc = new Y.Doc();
  const doc = pmDocFromMarkdown(markdown, profile, blockIds, hashes);
  prosemirrorToYXmlFragment(doc, ydoc.getXmlFragment(YDOC_FRAGMENT));
  return ydoc;
}
